import { useState, useRef, useEffect } from "react";
import { RefreshCw } from "lucide-react";
import { usePlatform } from "@/hooks/usePlatform";
import { hapticSuccess } from "@/utils/haptics";

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
  threshold?: number;
}

export function PullToRefresh({ 
  onRefresh, 
  children, 
  threshold = 80 
}: PullToRefreshProps) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const startY = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = usePlatform();

  useEffect(() => {
    // Only enable on mobile devices
    setIsEnabled(isMobile);
  }, [isMobile]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isEnabled || isRefreshing) return;
    
    // Only trigger if at top of scroll
    if (containerRef.current && containerRef.current.scrollTop === 0) {
      startY.current = e.touches[0].clientY;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isEnabled || isRefreshing || startY.current === 0) return;

    const currentY = e.touches[0].clientY;
    const distance = currentY - startY.current;

    if (distance > 0 && containerRef.current && containerRef.current.scrollTop === 0) {
      // Prevent default scrolling when pulling down
      e.preventDefault();
      
      // Apply resistance to pull
      const resistance = 2.5;
      setPullDistance(Math.min(distance / resistance, threshold * 1.5));
    }
  };

  const handleTouchEnd = async () => {
    if (!isEnabled || isRefreshing || pullDistance === 0) {
      setPullDistance(0);
      startY.current = 0;
      return;
    }

    if (pullDistance >= threshold) {
      setIsRefreshing(true);
      hapticSuccess();
      
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
        startY.current = 0;
      }
    } else {
      setPullDistance(0);
      startY.current = 0;
    }
  };

  const rotation = Math.min((pullDistance / threshold) * 360, 360);
  const opacity = Math.min(pullDistance / threshold, 1);
  const scale = Math.min(pullDistance / threshold, 1);

  return (
    <div
      ref={containerRef}
      className="relative h-full overflow-auto ios-momentum-scroll"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      {isEnabled && (
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 z-50 flex items-center justify-center transition-all"
          style={{
            transform: `translateX(-50%) translateY(${Math.min(pullDistance - 40, 20)}px) scale(${scale})`,
            opacity: opacity,
          }}
        >
          <div className="bg-background/90 backdrop-blur-sm rounded-full p-3 shadow-lg border border-border">
            <RefreshCw
              className={`h-6 w-6 text-primary ${isRefreshing ? 'animate-spin' : ''}`}
              style={{
                transform: isRefreshing ? 'none' : `rotate(${rotation}deg)`,
              }}
            />
          </div>
        </div>
      )}

      {children}
    </div>
  );
}
